import { useCallback, useEffect, useState, useRef } from 'react';

import { IVideoDimensions } from '../videoBox.types';

const DEFAULT_DIMENSIONS: IVideoDimensions = {
  displayWidth: 0,
  displayHeight: 0,
  offsetX: 0,
  offsetY: 0,
  scaleX: 1,
  scaleY: 1,
  videoWidth: 0,
  videoHeight: 0,
  containerWidth: 0,
  containerHeight: 0,
};

const useVideoDimensions = (
  videoRef: React.RefObject<HTMLVideoElement | HTMLCanvasElement | null>,
  containerRef: React.RefObject<HTMLElement | null>,
  isPipBox?: boolean
): IVideoDimensions => {
  const [dimensions, setDimensions] = useState<IVideoDimensions>(DEFAULT_DIMENSIONS);

  const observerRef = useRef<ResizeObserver>();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const animationFrameRef = useRef<number>();

  const calculateDimensions = useCallback(() => {
    const container = containerRef.current;
    const video = videoRef.current;

    // Early return if no container
    if (!container) {
      setDimensions(DEFAULT_DIMENSIONS);
      return;
    }

    // Get container dimensions
    let containerWidth = 0;
    let containerHeight = 0;
    try {
      const rect = container.getBoundingClientRect();
      containerWidth = rect.width;
      containerHeight = rect.height;
    } catch (error) {
      console.warn('Failed to get container dimensions:', error);
      setDimensions(DEFAULT_DIMENSIONS);
      return;
    }

    // Check if we have a video element
    const hasVideo = !!video && (video instanceof HTMLVideoElement || video instanceof HTMLCanvasElement);

    let displayWidth = containerWidth;
    let displayHeight = containerHeight;
    let offsetX = 0;
    let offsetY = 0;
    let scaleX = 1;
    let scaleY = 1;
    let videoWidth = 0;
    let videoHeight = 0;

    if (hasVideo) {
      // Get video dimensions
      if (video instanceof HTMLVideoElement) {
        videoWidth = video.videoWidth || 0;
        videoHeight = video.videoHeight || 0;
      } else if (video instanceof HTMLCanvasElement) {
        videoWidth = video.width || 0;
        videoHeight = video.height || 0;
      }

      // Only calculate aspect ratio positioning if we have valid video dimensions
      if (videoWidth > 0 && videoHeight > 0) {
        const videoAspect = videoWidth / videoHeight;
        const containerAspect = containerWidth / containerHeight;

        if (containerAspect > videoAspect) {
          // Video fits by height (letterbox on sides)
          displayHeight = containerHeight;
          displayWidth = containerHeight * videoAspect;
          offsetX = (containerWidth - displayWidth) / 2;
        } else {
          // Video fits by width (letterbox top/bottom)
          displayWidth = containerWidth;
          displayHeight = containerWidth / videoAspect;
          offsetY = (containerHeight - displayHeight) / 2;
        }

        scaleX = displayWidth / videoWidth;
        scaleY = displayHeight / videoHeight;
      }
      // If video exists but dimensions are 0, keep display at container size
    }
    // If no video, display fills container (already set above)

    setDimensions({
      displayWidth,
      displayHeight,
      offsetX,
      offsetY,
      scaleX,
      scaleY,
      videoWidth,
      videoHeight,
      containerWidth,
      containerHeight,
    });
  }, [containerRef, videoRef]);

  // Debounced calculation to prevent loops
  const debouncedCalculate = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);

    animationFrameRef.current = requestAnimationFrame(() => {
      timeoutRef.current = setTimeout(() => {
        calculateDimensions();
      }, 50); // 50ms debounce
    });
  }, [calculateDimensions]);

  // Setup observers and listeners
  useEffect(() => {
    const video = videoRef.current;

    // Initial calculation
    debouncedCalculate();

    // Video event listeners
    const videoListeners: { event: string; handler: () => void }[] = [];

    if (video instanceof HTMLVideoElement) {
      const handleVideoEvents = () => {
        debouncedCalculate();
      };

      // Listen to video events that indicate dimension changes
      ['loadedmetadata', 'resize'].forEach((event) => {
        video.addEventListener(event, handleVideoEvents);
        videoListeners.push({ event, handler: handleVideoEvents });
      });
    }

    // Setup ResizeObserver for container and video
    if (isPipBox) {
      // If we're rendering inside a PiP window, ResizeObserver can be unreliable across documents.
      // Use a throttled RAF polling loop instead to measure the element periodically.
      const pipLoop = () => {
        debouncedCalculate();
        animationFrameRef.current = requestAnimationFrame(pipLoop);
      };
      animationFrameRef.current = requestAnimationFrame(pipLoop);
    } else if (typeof ResizeObserver !== 'undefined') {
      observerRef.current = new ResizeObserver(() => {
        // Temporarily disconnect the observer to prevent recursive/looping
        // resize callbacks while we recalculate dimensions.
        observerRef.current?.disconnect();
        requestAnimationFrame(() => {
          // Recalculate video dimensions after the browser has completed layout.
          debouncedCalculate();
          // Re-observe the container (and video element if present) so future
          // size changes continue to trigger recalculation.
          if (containerRef.current?.isConnected && observerRef.current) {
            observerRef.current.observe(containerRef.current);
            if (video instanceof HTMLVideoElement) {
              observerRef.current.observe(video);
            }
          }
        });
      });

      // Observe container
      if (containerRef.current) {
        try {
          observerRef.current.observe(containerRef.current);
        } catch (error) {
          console.warn('Failed to observe container:', error);
        }
      }

      // Also observe video element for size changes
      if (video) {
        try {
          observerRef.current.observe(video);
        } catch (error) {
          console.warn('Failed to observe video:', error);
        }
      }
    } else {
      // Fallback for browsers without ResizeObserver
      window.addEventListener('resize', debouncedCalculate);
    }

    // Cleanup function
    return () => {
      // Cleanup video event listeners
      if (video instanceof HTMLVideoElement) {
        videoListeners.forEach(({ event, handler }) => {
          video.removeEventListener(event, handler);
        });
      }

      // Cleanup observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // Cleanup timers
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);

      // Cleanup window resize listener
      window.removeEventListener('resize', debouncedCalculate);
    };
  }, [videoRef, containerRef, debouncedCalculate, isPipBox]);

  return dimensions;
};

export default useVideoDimensions;
